import { Component, Inject, OnInit, DestroyRef, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { RecursoService } from 'src/app/services/recurso.service'
import { UnespCoreEnvironment, UnespCoreMessageService } from 'src/libs/unesp-core'
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../confirm-dialog/confirm-dialog.component'
import { NotaValidator } from 'src/app/utils/nota-validator'
import { ArquivoViewerAvaliacaoComponent } from '../../comissao-julgadora/arquivo-viewer-avaliacao/arquivo-viewer-avaliacao.component'
import { concatMap } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-recurso-notas',
  templateUrl: './recurso-notas.component.html',
  styleUrls: ['./recurso-notas.component.scss']
})
export class RecursoNotasComponent implements OnInit {
  private destroyRef = inject(DestroyRef)

  recursoId!: number
  resumoNotas: any
  loading = true
  isRecursoFase1PendenteAnalise = false
  isRecursoFase2PendenteAnalise = false
  arquivoParecer: File | null = null
  private baseUrl: string

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private recursoService: RecursoService,
    private messageService: UnespCoreMessageService,
    private dialog: MatDialog,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  ngOnInit(): void {
    this.recursoId = Number(this.route.snapshot.paramMap.get('id'))
    this.carregarNotas()
  }

  carregarNotas(): void {
    this.loading = true
    this.recursoService.obterNotasInscricaoRecurso(this.recursoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.resumoNotas = data
        this.isRecursoFase1PendenteAnalise = !data.recursoFase2 && data.parecerRecurso === 'Em Análise'
        this.isRecursoFase2PendenteAnalise = data.recursoFase2 && data.parecerRecurso === 'Em Análise'
        this.loading = false
      },
      error: (error) => {
        this.messageService.showMessageError(error.error?.message || 'Erro ao carregar notas')
        this.loading = false
      }
    })
  }

  deferirRecurso(): void {
    if (!this.arquivoParecer) {
      this.messageService.showMessageError('Anexe o parecer em PDF para deferir')
      return
    }
    const isFase2 = this.isRecursoFase2PendenteAnalise
    const subtitulo = isFase2
      ? 'O recurso será deferido e encerrado. Deseja continuar?'
      : 'Ao deferir o recurso, você poderá alterar as notas do candidato. Deseja continuar?'
    const mensagemConfirmacao = `${subtitulo}`
      + '<div class="card-warning dialog-alert-banner">⚠️ <strong>Atenção:</strong> O parecer que será enviado ao SIGAD deve conter a assinatura de todos os membros da comissão julgadora.</div>'
    const confirmDialog = new ConfirmDialogModel('Confirmar deferimento', mensagemConfirmacao)
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        this.recursoService.uploadAnexoParecerPresidente(this.recursoId, this.arquivoParecer!).pipe(
          concatMap(() => this.recursoService.emitirParecerRecurso(this.recursoId, 'Deferido'))
        , takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            if (isFase2) {
              this.messageService.showMessageSuccess('Recurso deferido com sucesso')
              this.router.navigate(['/comissao/recursos-pendentes'])
            } else {
              this.messageService.showMessageSuccess('Recurso deferido. Altere as notas abaixo.')
              this.isRecursoFase1PendenteAnalise = false
              this.arquivoParecer = null
              this.carregarNotas()
            }
          },
          error: (error) => {
            this.messageService.showMessageError(error.error?.message || 'Erro ao deferir recurso')
          }
        })
      }
    })
  }

  indeferirRecurso(): void {
    if (!this.arquivoParecer) {
      this.messageService.showMessageError('Anexe o parecer em PDF para indeferir')
      return
    }
    const confirmDialog = new ConfirmDialogModel(
      'Confirmar indeferimento',
      'O recurso será indeferido e encerrado. Deseja continuar?'
      + '<div class="card-warning dialog-alert-banner">⚠️ <strong>Atenção:</strong> O parecer que será enviado ao SIGAD deve conter a assinatura de todos os membros da comissão julgadora.</div>'
    )
    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        this.recursoService.uploadAnexoParecerPresidente(this.recursoId, this.arquivoParecer!).pipe(
          concatMap(() => this.recursoService.emitirParecerRecurso(this.recursoId, 'Indeferido'))
        , takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.showMessageSuccess('Recurso indeferido com sucesso')
            this.router.navigate(['/comissao/recursos-pendentes'])
          },
          error: (error) => {
            this.messageService.showMessageError(error.error?.message || 'Erro ao indeferir recurso')
          }
        })
      }
    })
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      const file = input.files[0]
      if (file.type !== 'application/pdf') {
        this.messageService.showMessageError('Formato inválido. Selecione um arquivo PDF.')
        input.value = ''
        return
      }
      this.arquivoParecer = file
    }
  }

  voltar(): void {
    this.router.navigate(['/comissao/recursos-pendentes'])
  }

  getExaminadores(): any[] {
    return this.resumoNotas.notasPorExaminador || []
  }

  getProvas(): string[] {
    if (!this.resumoNotas.notasPorExaminador?.length) return []
    const provasSet = new Set<string>()
    this.resumoNotas.notasPorExaminador.forEach((examinador: any) => {
      Object.keys(examinador.notas).forEach(prova => provasSet.add(prova))
    })
    return Array.from(provasSet)
  }

  getNota(examinador: any, prova: string): any {
    return examinador.notas?.[prova] || '-'
  }

  getMediaExaminador(examinador: any): any {
    const notas = Object.values(examinador.notas).filter((n: any) => n != null)
    if (notas.length === 0) return '-'
    const soma = notas.reduce((acc: number, nota: any) => acc + Number(nota), 0)
    return (soma / notas.length).toFixed(2)
  }

  getDisplayedColumns(): string[] {
    return ['examinador', ...this.getProvas()]
  }

  getMatricula(examinador: any): string {
    return examinador.matricula || ''
  }

  onNotaChange(examinador: any, prova: string, event: any): void {
    const nota = examinador.notas[prova]
    if (nota !== null && nota !== undefined && nota !== '') {
      const validation = NotaValidator.isNotaValid(Number(nota))
      if (!validation.valid) {
        this.messageService.showMessageError(validation.message!)
        examinador.notas[prova] = null
        event.target.value = ''
      }
    }
  }

  salvarNotas(): void {
    const confirmDialog = new ConfirmDialogModel(
      'Confirmar retificação de notas',
      'As notas serão alteradas e a classificação será recalculada. Deseja continuar?'
    )

    const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: confirmDialog })

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
      if (res) {
        const notasParaEnviar = this.resumoNotas.notasPorExaminador.flatMap((examinador: any) =>
          Object.entries(examinador.notas).map(([tipoProva, nota]) => ({
            comissaoJulgadoraId: examinador.comissaoId,
            tipoProva,
            nota
          }))
        )

        this.recursoService.atualizarNotasRecurso(this.recursoId, notasParaEnviar).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response: any) => {
            this.messageService.showMessageSuccess('Notas atualizadas com sucesso')
            if (response?.todosRecursosConcluidos) {
              this.router.navigate(['/comissao-julgadora/resultado/classificacao', this.resumoNotas.concursoId])
            } else {
              this.router.navigate(['/comissao/recursos-pendentes'])
            }
          },
          error: (error) => {
            this.messageService.showMessageError(error.error?.message || 'Erro ao atualizar notas')
          }
        })
      }
    })
  }

  visualizarDocumentosComplementares(): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/comissao-julgadora/visualizar-complementos', this.resumoNotas.inscricaoId])
    );
    window.open(url, '_blank');
  }

  visualizarProvaEscrita(): void {
    const url = `${this.baseUrl}/comissao-prova/inscricaodocumento/${this.resumoNotas.idArquivoProva}/${this.resumoNotas.arquivoProva}`
    this.abrirViewer('Prova Escrita', url)
  }

  visualizarAnexo(): void {
    const url = `${this.baseUrl}/comissao/recurso/${this.recursoId}/anexo/${this.resumoNotas.anexoRecurso}`
    this.abrirViewer('Anexo do Recurso', url)
  }

  private abrirViewer(titulo: string, url: string): void {
    this.dialog.open(ArquivoViewerAvaliacaoComponent, {
      data: { tituloDoArquivo: titulo, urlDoArquivo: url },
      width: '98vw', height: '96vh', maxWidth: '100vw', panelClass: 'fullheight-dialog'
    })
  }
}
